import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ContractInputsContainer } from './ContractInputsContainer';
import { SignatureSidebarInputs } from 'src/components/Signature/SignatureSidebar';
import { BANNER_HEIGHT, DESKTOP_APP_BAR_HEIGHT } from 'src/constants';
import { NonHoverBorder } from 'src/theme/colors';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { VariableInputs } from './VariableInputs';
import { AutofillInputs } from './AutofillInputs';
import { RootState } from 'src/store';

export const CONTRACT_SIDEBAR_WIDTH = 300;

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    top: DESKTOP_APP_BAR_HEIGHT,
    right: 0,
    width: CONTRACT_SIDEBAR_WIDTH,
    height: `calc(100vh - ${DESKTOP_APP_BAR_HEIGHT}px)`,
    borderLeft: `1px solid ${NonHoverBorder}`,
    overflow: 'auto',
  },
  withTrialBanner: {
    height: `calc(100vh - ${DESKTOP_APP_BAR_HEIGHT + BANNER_HEIGHT}px)`,
    top: DESKTOP_APP_BAR_HEIGHT + BANNER_HEIGHT,
  },
}));

/**
 *  Renders the right sidebar for creating contract templates.
 *  This sidebar includes different types of draggable inputs.
 *
 * @returns {JSX.Element} The JSX element rendering the contract template builder sidebar.
 */
export const ContractBuilderSidebar = () => {
  const classes = useStyles();
  const bannerOptions = useSelector(
    (state: RootState) => state.ui.bannerOptions,
  );
  const { placeholderElement } = useContext(ContractBuilderContext);

  // List of draggable input sections for the contract sidebar
  const contractSidebarSections = [
    {
      title: 'Your Fixed Inputs',
      tooltip:
        'Add your signature, initials, date or custom text. These values will always be used every time you share the contract with a client.',
      Component: (
        <SignatureSidebarInputs
          signaturePlaceholderComponentElement={placeholderElement}
          forContracts
        />
      ),
    },
    {
      title: 'Your Variable Inputs',
      tooltip:
        "Each time you share a contract with a client, you'll be prompted to provide these values.",
      Component: <VariableInputs />,
    },
    {
      title: 'Client Autofill Fields',
      tooltip:
        'The values will automatically be set based on the client you share a contract with.',
      Component: <AutofillInputs />,
    },
    {
      title: 'Client Inputs',
      tooltip:
        'Your client will be prompted to provide these inputs when they complete the contract.',
      Component: (
        <SignatureSidebarInputs
          isClientSignatureButton
          signaturePlaceholderComponentElement={placeholderElement}
          forContracts
        />
      ),
    },
  ];

  return (
    <div
      className={classNames(classes.root, {
        // adjust sidebar height and top offset when trial banner is visible
        [classes.withTrialBanner]: Boolean(bannerOptions),
      })}
    >
      {contractSidebarSections.map(({ title, tooltip, Component }) => (
        <ContractInputsContainer summary={title} tooltip={tooltip}>
          {Component}
        </ContractInputsContainer>
      ))}
    </div>
  );
};
